import { usePermissionContext, AuthAction } from "@/react/providers/permission-context-provider";
import { Plus } from "@procore/core-icons";
import { useI18nContext, FlexList, Button } from "@procore/core-react";
import React from "react";
import {
   useAssignmentTearsheet,
   onCreateAssignmentClick,
} from "../tearsheets/assignment/assignment-tearsheet";
import type { CustomField } from "@/react/prop-types";
import type { ExportFormValues } from "../data-table/DataExport/export-modal";
import { ExportModal } from "../data-table/DataExport/export-modal";
import { useExportToastContext } from "../data-table/DataExport/FileDownload";
import type { AppliedRoleOption } from "@laborchart-modules/lc-core-api/dist/api/projects/get-applied-role-options";
import { createAssignmentListReport } from "./queries";

export type AssignmentListQuickControlsProps = {
   groupId: string;
   customFields?: CustomField[] | null;
   isLastNameFirst: boolean;
   roleOptions: AppliedRoleOption[];
   configKey: string;
   onRefreshCustomFields: () => void;
};

export const AssignmentListQuickControls: React.FC<AssignmentListQuickControlsProps> = ({
   configKey,
   groupId,
   customFields,
   roleOptions,
   isLastNameFirst,
   onRefreshCustomFields,
}) => {
   const { checkAuthAction } = usePermissionContext();
   const I18n = useI18nContext();
   const [canCreateAssignment, setCanCreateAssignment] = React.useState(false);
   const { dispatch: assignmentTearsheetDispatch } = useAssignmentTearsheet();
   const { setVisible: showFileDownloadToast, addItems: addDownloadableReport } =
      useExportToastContext();

   React.useEffect(() => {
      setCanCreateAssignment(checkAuthAction(AuthAction.MANAGE_ASSIGNMENTS));
   }, [checkAuthAction]);

   const onAssignmentListExport = React.useCallback(
      async (params: ExportFormValues) => {
         const report = await createAssignmentListReport({
            formValues: params,
            groupId,
            configKey,
            I18n,
            customFields,
            roleOptions,
            isLastNameFirst,
            search: null, // there is no search box in assignment list page
         });
         if (!report) {
            return;
         }
         showFileDownloadToast(true);
         addDownloadableReport([report.data]);
      },
      [customFields],
   );

   return (
      <FlexList size="xs" alignItems="center" style={{ marginLeft: "auto" }}>
         {canCreateAssignment && (
            <Button
               onClick={() => {
                  onCreateAssignmentClick(assignmentTearsheetDispatch);
                  onRefreshCustomFields();
               }}
               icon={<Plus />}
            >
               {I18n.t("views.company.workforce_planning.create")}
            </Button>
         )}
         <ExportModal onExport={onAssignmentListExport} />
      </FlexList>
   );
};
